import type { LucideIcon } from "lucide-react";
import {
  CalendarCheck, Car, Users, FileText, CreditCard,
  QrCode, BarChart3, Settings, Bell, ClipboardCheck, ArrowRightLeft, History,
  Smartphone, Globe, Send, TrendingUp, Wrench, CalendarDays,
  IdCard, FileSignature, Printer, HandCoins, ShieldCheck, Timer,
  Camera, Zap, FileSpreadsheet,
  Satellite, MessageSquare, Banknote, Cpu, Calculator,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export type FeatureItem = { name: string; icon: LucideIcon; desc: string };

// Live, shipping modules (sourced from the RentOS Business App spec)
export const PHASE_1: FeatureItem[] = [
  { name: "Booking lifecycle", icon: CalendarCheck, desc: "Reserved to completed, with ordered pickup, return and settlement actions." },
  { name: "Pickup Checklist", icon: ClipboardCheck, desc: "Odometer and photo, fuel, at least four vehicle photos, ID, existing damage and accessories." },
  { name: "Return & Settlement", icon: ArrowRightLeft, desc: "Record the return first, then generate rent, grace-based late charges, extra kilometres and added charges." },
  { name: "Live Trip Monitoring", icon: Timer, desc: "Elapsed time, expected return, grace period and overdue status with a running overtime estimate." },
  { name: "Deposit Settlement", icon: HandCoins, desc: "Track held deposits separately. Deduct charges or record a refund against the booking." },
  { name: "Fleet & Status", icon: Car, desc: "Bikes, scooters and cars with available, booked, out-for-rental, maintenance and blocked states." },
  { name: "Vehicle Documents", icon: FileText, desc: "Store RC, insurance and PUC. Expired insurance or PUC blocks new bookings." },
  { name: "Maintenance & Damage", icon: Wrench, desc: "Log damage, repairs, services and incidents with severity, status, cost and vehicle history." },
  { name: "Customer Records", icon: Users, desc: "Encrypted phone and email, customer status, rental history and lifetime spend." },
  { name: "KYC with Consent", icon: IdCard, desc: "Manually verify documents at the counter, record consent and audit changes. Unverified KYC blocks pickup." },
  { name: "Digital Agreements", icon: FileSignature, desc: "Pre-filled booking agreements signed on screen, stored with the booking and optionally emailed." },
  { name: "Payment Recording", icon: CreditCard, desc: "Record cash, UPI, card and bank transfers through bookings. RentOS does not process payments." },
  { name: "Revenue Ledger", icon: BarChart3, desc: "Read-only payment ledger separates rental income, penalties, deposits held and deposits refunded." },
  { name: "Reports & Analytics", icon: TrendingUp, desc: "Revenue, booking volume, utilisation, vehicle mix, top customers and idle vehicles by date range." },
  { name: "Notifications", icon: Bell, desc: "In-app overdue, no-show, document expiry and subscription alerts, plus push on the native apps." },
  { name: "Activity Log", icon: History, desc: "Timestamped action history, filterable by level and entity, with an immutable record." },
  { name: "Vehicle QR Codes", icon: QrCode, desc: "Download or print a QR code that links directly to a vehicle’s page." },
  { name: "Android & iOS", icon: Smartphone, desc: "The same Business app with native camera, file picker, sharing and push notifications." },
  { name: "Business Emails", icon: Send, desc: "Booking emails and pickup/return reminders from your own SMTP address when the email module is enabled." },
  { name: "CSV Import & Export", icon: FileSpreadsheet, desc: "Map and validate vehicle/customer imports. Export bookings, payments, customers and vehicles." },
  { name: "Branded Invoices", icon: Printer, desc: "Print-ready booking invoices with billing breakdown, payment history, deposit summary and signature." },
  { name: "Settings & Policies", icon: Settings, desc: "Business settings, grace period and notification preferences. Settings and reports are always available." },
];

export const ROADMAP: FeatureItem[] = [
  { name: "Web app", icon: Globe, desc: "Use app.rentos.tech on desktop or any phone browser." },
  { name: "Android app", icon: Smartphone, desc: "Run the Business app with native camera, sharing and push notifications." },
  { name: "iOS app", icon: Smartphone, desc: "The same bookings, fleet, KYC and settlement workflows on iPhone." },
];

// Exploring / on the horizon — clearly NOT live. Labelled as upcoming on the
// features page so we never imply these ship today.
export const UPCOMING: FeatureItem[] = [
  { name: "Live GPS vehicle tracking", icon: Satellite, desc: "Real-time location and trip routes for your fleet, straight on the dashboard." },
  { name: "WhatsApp notifications", icon: MessageSquare, desc: "Send booking, pickup and return updates to customers over WhatsApp." },
  { name: "Online payments & deposits", icon: Banknote, desc: "Collect rent and refundable deposits online with instant reconciliation." },
  { name: "Customer self-booking", icon: Globe, desc: "A public booking link so customers can request a vehicle themselves." },
  { name: "Telematics & IoT locks", icon: Cpu, desc: "Integrate GPS devices and smart locks for keyless, trackable handovers." },
  { name: "Accounting exports", icon: Calculator, desc: "Sync revenue, deposits and invoices with popular accounting tools." },
];

// "Why RentOS" — outcome-led
export const WHY: { title: string; desc: string; icon: LucideIcon }[] = [
  { title: "Guided bookings", desc: "Schedule, vehicle, pricing, customer and confirmation in one guided flow.", icon: Zap },
  { title: "Recorded settlement", desc: "Overtime, extra KM and damages billed automatically. The system does the math, every time.", icon: TrendingUp },
  { title: "Photo-backed handovers", desc: "At least four vehicle photos, odometer shots and signed agreements stored against every trip.", icon: Camera },
  { title: "Deposit lifecycle, recorded", desc: "Held separately, then deducted or refunded at settlement — linked to the booking and payment record.", icon: HandCoins },
  { title: "Live overdue alerts", desc: "Trip timers, grace windows and overdue states refresh automatically for the owner.", icon: Timer },
  { title: "Document compliance", desc: "Expired insurance or PUC blocks new bookings with the reason shown.", icon: ShieldCheck },
  { title: "Mobile-ready counter", desc: "Capture photos and forms from any phone — on the web or in the native Android and iOS app.", icon: Smartphone },
  { title: "Audit-grade history", desc: "Timestamped actions preserved in an immutable activity history.", icon: History },
];

// Stats — calibrated for an India-first fleet SaaS launch.
// Static fallback; the StatsBand uses these fixed, honest product facts.
export const STATS = [
  { label: "Owner per business", value: 1, suffix: "" },
  { label: "Plan-controlled modules", value: 8, suffix: "" },
  { label: "Vehicle photos per checklist, minimum", value: 4, suffix: "" },
  { label: "Days of subscription grace", value: 3, suffix: "" },
];

// Homepage spotlight — product principles (static; not customer quotes).
export const TESTIMONIALS = [
  {
    name: "The rental loop",
    role: "Bookings → pickup → return → settlement",
    quote: "Every rental follows a recorded flow: verify the customer, collect the deposit, capture vehicle condition, monitor the trip and settle the booking.",
  },
  {
    name: "Your money, separated",
    role: "Payment records and deposit ledger",
    quote: "Refundable deposits stay separate from earned income. Only the deducted portion counts as revenue; the refunded portion does not.",
  },
  {
    name: "Your business records",
    role: "CSV import and export",
    quote: "Import vehicles and customers with a validation preview. Export bookings, payments, customers and vehicles with date-range and column selection.",
  },
];

export const TRUSTED_BY = ["Bikes", "Scooters", "Self-drive cars", "Web app", "Android", "iOS"];

// Detailed end-to-end workflows
export const WORKFLOWS = [
  {
    key: "booking",
    name: "Booking → Pickup",
    icon: CalendarCheck,
    summary: "Create a booking through schedule, vehicle, pricing, customer and confirmation.",
    steps: [
      "Pick start and end window — instant or scheduled is detected automatically.",
      "Select a bookable vehicle. Live availability check flags conflicts and turnaround buffers.",
      "Set rent, deposit and KM limit. Live payment breakup updates as you type.",
      "Attach an existing customer or add one inline — customer details captured in the booking.",
      "Confirm the booking; configured business email can send a confirmation. Complete KYC, checklist and deposit before pickup.",
    ],
  },
  {
    key: "trip",
    name: "Live Trip Monitoring",
    icon: Timer,
    summary: "Live timer with on-time, grace and overdue states that refresh automatically.",
    steps: [
      "Trip timer shows elapsed, expected return and remaining time at a glance.",
      "Color states: green on-time, amber grace period, red overdue with an overtime estimate.",
      "Extend a booking in one click with hours and reason — extra charge auto-computed.",
      "Extensions check the next booking for conflicts before updating the trip.",
      "Overdue trips surface on the owner’s dashboard.",
    ],
  },
  {
    key: "return",
    name: "Return & Auto-Billing",
    icon: ArrowRightLeft,
    summary: "Two-phase flow — return checklist then a fully-calculated billing breakdown.",
    steps: [
      "Capture a return odometer above the pickup reading, fuel level and at least four vehicle photos.",
      "Record new damage with description, location, photo and estimated repair cost.",
      "Reconcile accessories returned vs issued — missing items flagged automatically.",
      "Billing engine computes rent + grace-based late charges + extra kilometres + added charges. Fuel is recorded, not billed.",
      "Collect per-charge in cash, UPI, card or bank — and settle the deposit.",
    ],
  },
  {
    key: "deposit",
    name: "Deposit Settlement",
    icon: HandCoins,
    summary: "Hold deposits separately, then deduct charges or refund the balance at settlement.",
    steps: [
      "Deposit collected at pickup is held against the booking with a clear status.",
      "Return-time charges can be set to 'deduct from deposit' instead of collected separately.",
      "Record a full or partial refund, or deduct charges — refund payments stay linked to the booking.",
      "Status transitions: HELD → PARTIALLY_DEDUCTED → FULLY_DEDUCTED → REFUNDED.",
      "Only the deducted deposit portion counts as revenue; refund records remain linked to the booking.",
    ],
  },
  {
    key: "kyc",
    name: "KYC & Agreement",
    icon: ShieldCheck,
    summary: "Inline document capture and on-screen rental agreement before the trip starts.",
    steps: [
      "Upload DL, ID proof or passport directly from the pickup screen.",
      "Confirm customer consent, then manually verify or reject KYC; every change is audited.",
      "Pre-filled rental agreement loads customer, vehicle, dates and amount automatically.",
      "Customer signs on screen with finger or stylus — agreement stored against the booking.",
      "Consent confirmation and verified KYC are required before a trip can start.",
    ],
  },
  {
    key: "compliance",
    name: "Documents & Compliance",
    icon: FileText,
    summary: "RC, insurance and PUC expiries auto-block bookability before they cost you fines.",
    steps: [
      "Store RC, insurance and PUC scans with policy numbers and expiry dates.",
      "Insurance and PUC expiry dates are monitored automatically.",
      "Expired insurance or PUC blocks the vehicle from new bookings.",
      "An expiry alert is raised for the business owner.",
      "Keep current document records on the vehicle.",
    ],
  },
];

// Differentiators (paper register vs RentOS)
export const DIFFERENTIATORS = [
  { before: "Paper registers and WhatsApp screenshots", after: "Structured digital bookings with a recorded lifecycle" },
  { before: "Manual overtime and KM math on a calculator", after: "Generate the bill after recording the return" },
  { before: "Customer disputes with no evidence", after: "At least four photos at pickup and return" },
  { before: "Deposit notebooks across four pages", after: "Held, deducted, refunded with an audit log" },
  { before: "Missed insurance and PUC expiries", after: "Blocks new bookings on insurance/PUC expiry" },
  { before: "Generic ERP built for warehouses", after: "Built for Indian two-wheeler and car fleets" },
];

export const IMPACT_METRICS = [
  { label: "Bookings", before: "Register and messages", after: "Availability-checked flow" },
  { label: "Payments", before: "UPI screenshots", after: "Booking-linked records" },
  { label: "Condition", before: "Scattered photos", after: "Pickup and return checklists" },
  { label: "Deposits", before: "Mixed with income", after: "Separate ledger" },
  { label: "Documents", before: "Manual expiry checks", after: "Insurance/PUC blocking" },
  { label: "Reports", before: "Manual totals", after: "Date-range analytics" },
];

// Detailed page-level breakdown for the Features page
export const PAGE_BREAKDOWN = [
  {
    key: "booking-detail",
    name: "Booking Detail",
    path: "/bookings/:id",
    icon: CalendarCheck,
    summary: "Status-aware screen that adapts to every lifecycle stage — Reserved, Confirmed, Active, Return, Settlement and Completed.",
    bullets: [
      "Adaptive header with booking ID, status, type, duration and amount due.",
      "Pickup checklist with odometer, fuel, photos, damages and payment.",
      "Live trip card with on-time, grace and overdue states.",
      "Return & billing flow with deposit settlement and per-charge collection.",
      "Sign the agreement against the booking and print the invoice with its settlement records.",
    ],
  },
  {
    key: "new-booking",
    name: "New Booking Wizard",
    path: "/bookings/new",
    icon: CalendarDays,
    summary: "Five-step structured flow with live availability and pricing preview.",
    bullets: [
      "Schedule with automatic instant or scheduled detection.",
      "Vehicle picker filtered to bookable units with conflict detection.",
      "Pricing with rent, deposit, KM limit and extra-KM rate.",
      "Customer search or inline create with phone uniqueness.",
      "Confirmation with terms-explained acknowledgement.",
    ],
  },
  {
    key: "vehicles",
    name: "Vehicles & Fleet",
    path: "/vehicles",
    icon: Car,
    summary: "Grid of every vehicle with status, documents, damage and maintenance history.",
    bullets: [
      "View vehicles by their status and type.",
      "Per-vehicle tabs: Overview, Documents, Maintenance, Damage, Media, Bookings.",
      "Document expiry tracking with auto-block on lapse.",
      "Cover image, gallery and pre/post-rental photo storage.",
      "CSV import with column mapping and a validation preview.",
    ],
  },
  {
    key: "customers",
    name: "Customers",
    path: "/customers",
    icon: Users,
    summary: "Customer profiles with KYC, status, rental history and spend.",
    bullets: [
      "Behavioural status: Trusted, Active, Risky, Blacklisted.",
      "KYC queue with verify/reject and permanent audit trail.",
      "Document vault for DL, ID proof and passport.",
      "Rental history and lifetime spend update when a booking completes.",
      "Bulk CSV import and instant text search.",
    ],
  },
  {
    key: "payments",
    name: "Payments",
    path: "/payments",
    icon: CreditCard,
    summary: "Every rupee in and out — rental, deposit, refund and penalty.",
    bullets: [
      "Revenue cards: Total, Rental, Penalty, Deposits Held, Deposits Refunded.",
      "Method icons for Cash, UPI, Card and Bank Transfer.",
      "Status badges: Completed, Pending, Refunded.",
      "Filter by type, method and date range.",
      "Deposit refund flow linked to the original booking.",
    ],
  },
  {
    key: "reports",
    name: "Reports & Analytics",
    path: "/reports",
    icon: BarChart3,
    summary: "Revenue, utilisation, customers and fleet — across any date range.",
    bullets: [
      "Daily revenue trends and method-wise breakdown.",
      "Fleet utilisation gauge and type distribution.",
      "Top customers, booking-volume trends and payment-method breakdown.",
      "Most and least utilised vehicles ranked.",
      "Preset ranges or custom date pickers.",
    ],
  },
  {
    key: "settings",
    name: "Settings",
    path: "/settings",
    icon: Settings,
    summary: "Business profile, grace period, email settings and notification preferences.",
    bullets: [
      "Custom rental policies embedded into every agreement.",
      "Grace period control (0 = strict billing).",
      "Business-owned SMTP settings for branded booking emails.",
      "Granular notification toggles per event.",
      "Plan and usage visibility built in.",
    ],
  },
  {
    key: "qr",
    name: "QR Access",
    path: "/qr-access",
    icon: QrCode,
    summary: "Downloadable and printable QR codes that link to each vehicle page.",
    bullets: [
      "Generate and download QR codes per vehicle.",
      "Deep-link to the vehicle’s page in the Business app.",
      "Use the code to open the vehicle record.",
      "Vehicle availability remains part of the booking flow.",
      "Share or print the downloaded code.",
    ],
  },
];
